import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent } from '../components/ui/collapsible';
import api from '../lib/api';
import { usePersistentFilters } from '../lib/persistentFilters';
import { toast } from 'sonner';
import { Search, PackageCheck, Plus, Trash2, Loader2, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';
import { GreyReceiptForm } from '../components/forms';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';
import { useCompany } from '../contexts/CompanyContext';

const GreyReceiptsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedOrderId = searchParams.get('orderId');
  const { hasModulePermission } = usePermissions();
  const { activeCompany } = useCompany();
  
  const [receipts, setReceipts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [lrs, setLRs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = usePersistentFilters('greyReceipts.search', '');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = usePersistentFilters('greyReceipts.filters', {
    bill_id: '',
    lr_id: '',
    location_id: '',
    supplier_id: '',
    quality_id: '',
    subquality_id: '',
    received_date_from: '',
    received_date_to: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeCompany?.company_id]);

  useEffect(() => {
    if (preSelectedOrderId) {
      setShowAddForm(true);
    }
  }, [preSelectedOrderId]);

  const fetchData = async () => {
    try {
      const [receiptsRes, ordersRes, billsRes, lrsRes, suppliersRes, qualitiesRes, subqualitiesRes, locationsRes] = await Promise.allSettled([
        api.get('/grey-receipts'),
        api.get('/grey-orders'),
        api.get('/grey-bills'),
        api.get('/lr-reports'),
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities'),
        api.get('/locations')
      ]);
      setReceipts(receiptsRes.status === 'fulfilled' ? receiptsRes.value.data : []);
      setOrders(ordersRes.status === 'fulfilled' ? ordersRes.value.data : []);
      setBills(billsRes.status === 'fulfilled' ? billsRes.value.data : []);
      setLRs(lrsRes.status === 'fulfilled' ? lrsRes.value.data : []);
      setSuppliers(suppliersRes.status === 'fulfilled' ? suppliersRes.value.data : []);
      setQualities(qualitiesRes.status === 'fulfilled' ? qualitiesRes.value.data : []);
      setSubqualities(subqualitiesRes.status === 'fulfilled' ? subqualitiesRes.value.data : []);
      setLocations(locationsRes.status === 'fulfilled' ? locationsRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load receipts');
      setLoading(false);
    }
  };

  const getOrderDetails = (orderId) => {
    return orders.find(o => o.order_id === orderId) || {};
  };

  // Apply filters
  const filteredReceipts = receipts.filter(receipt => {
    const order = getOrderDetails(receipt.order_id);
    
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        receipt.receipt_number?.toLowerCase().includes(searchLower) ||
        receipt.verified_by?.toLowerCase().includes(searchLower) ||
        receipt.location_name?.toLowerCase().includes(searchLower) ||
        order.order_number?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Advanced filters
    if (filters.bill_id && receipt.bill_id !== filters.bill_id) return false;
    if (filters.lr_id && receipt.lr_id !== filters.lr_id) return false;
    if (filters.location_id && receipt.location_id !== filters.location_id) return false;
    if (filters.supplier_id && order.supplier_id !== filters.supplier_id) return false;
    if (filters.quality_id && order.quality_id !== filters.quality_id) return false;
    if (filters.subquality_id && order.subquality_id !== filters.subquality_id) return false;
    
    // Date range filters
    if (filters.received_date_from) {
      const recDate = new Date(receipt.received_date);
      const fromDate = new Date(filters.received_date_from);
      if (recDate < fromDate) return false;
    }
    if (filters.received_date_to) {
      const recDate = new Date(receipt.received_date);
      const toDate = new Date(filters.received_date_to);
      if (recDate > toDate) return false;
    }
    
    return true;
  });

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/grey-receipts/${deleteTarget.receipt_id}`);
      toast.success('Receipt deleted successfully!');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
    }
    setDeleting(false);
  };

  const resetFilters = () => {
    setFilters({
      bill_id: '',
      lr_id: '',
      location_id: '',
      supplier_id: '',
      quality_id: '',
      subquality_id: '',
      received_date_from: '',
      received_date_to: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const gateStatusColors = {
    'Received': 'bg-green-50 text-green-700 border-green-200',
    'Partially Received': 'bg-amber-50 text-amber-700 border-amber-200',
    'Approved': 'bg-green-50 text-green-700 border-green-200',
    'Pending Inspection': 'bg-amber-50 text-amber-700 border-amber-200',
    'Rejected': 'bg-red-50 text-red-700 border-red-200',
    'Conditional': 'bg-blue-50 text-blue-700 border-blue-200'
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="grey-receipts-list" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Grey Receipts
            </h1>
            <p className="text-slate-600 mt-1">{filteredReceipts.length} receipts</p>
          </div>
          
          <PermissionGuard module="grey_receipts" permission="create">
            <Sheet open={showAddForm} onOpenChange={setShowAddForm}>
              <SheetTrigger asChild>
                <Button data-testid="add-receipt-btn" className="bg-slate-900 hover:bg-slate-800">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Grey Receipt
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Add Grey Receipt</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <GreyReceiptForm 
                    orderId={preSelectedOrderId}
                    onSuccess={handleAddSuccess}
                    onCancel={() => setShowAddForm(false)}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </PermissionGuard>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-receipts-input"
              placeholder="Search by receipt number, verified by, order..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="h-11"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-slate-900 text-white">{activeFilterCount}</Badge>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent>
            <Card className="mb-4 border border-slate-200">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Bill</Label>
                    <Select value={filters.bill_id} onValueChange={(v) => setFilters({...filters, bill_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All bills" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All bills</SelectItem>
                        {bills.map(b => (
                          <SelectItem key={b.bill_id} value={b.bill_id}>{b.bill_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">LR</Label>
                    <Select value={filters.lr_id} onValueChange={(v) => setFilters({...filters, lr_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All LRs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All LRs</SelectItem>
                        {lrs.map(lr => (
                          <SelectItem key={lr.lr_id} value={lr.lr_id}>{lr.lr_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Location</Label>
                    <Select value={filters.location_id} onValueChange={(v) => setFilters({...filters, location_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All locations" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All locations</SelectItem>
                        {locations.map(loc => (
                          <SelectItem key={loc.location_id} value={loc.location_id}>{loc.location_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Supplier</Label>
                    <Select value={filters.supplier_id} onValueChange={(v) => setFilters({...filters, supplier_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All suppliers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All suppliers</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Quality</Label>
                    <Select value={filters.quality_id} onValueChange={(v) => setFilters({...filters, quality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All qualities</SelectItem>
                        {qualities.map(q => (
                          <SelectItem key={q.quality_id} value={q.quality_id}>{q.quality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Sub Quality</Label>
                    <Select value={filters.subquality_id} onValueChange={(v) => setFilters({...filters, subquality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All sub qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sub qualities</SelectItem>
                        {subqualities.map(sq => (
                          <SelectItem key={sq.subquality_id} value={sq.subquality_id}>{sq.subquality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Received Date From</Label>
                    <Input
                      type="date"
                      value={filters.received_date_from}
                      onChange={(e) => setFilters({...filters, received_date_from: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Received Date To</Label>
                    <Input
                      type="date"
                      value={filters.received_date_to}
                      onChange={(e) => setFilters({...filters, received_date_to: e.target.value})}
                      className="h-9"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end mt-4 gap-2">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.supplier_id && (
              <Badge variant="secondary" className="gap-1">
                Supplier: {suppliers.find(s => s.supplier_id === filters.supplier_id)?.name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, supplier_id: ''})} />
              </Badge>
            )}
            {filters.quality_id && (
              <Badge variant="secondary" className="gap-1">
                Quality: {qualities.find(q => q.quality_id === filters.quality_id)?.quality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, quality_id: ''})} />
              </Badge>
            )}
            {filters.subquality_id && (
              <Badge variant="secondary" className="gap-1">
                Sub Quality: {subqualities.find(sq => sq.subquality_id === filters.subquality_id)?.subquality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, subquality_id: ''})} />
              </Badge>
            )}
            {filters.bill_id && (
              <Badge variant="secondary" className="gap-1">
                Bill: {bills.find(b => b.bill_id === filters.bill_id)?.bill_number}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, bill_id: ''})} />
              </Badge>
            )}
            {filters.lr_id && (
              <Badge variant="secondary" className="gap-1">
                LR: {lrs.find(lr => lr.lr_id === filters.lr_id)?.lr_number}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, lr_id: ''})} />
              </Badge>
            )}
            {filters.location_id && (
              <Badge variant="secondary" className="gap-1">
                Location: {locations.find(loc => loc.location_id === filters.location_id)?.location_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, location_id: ''})} />
              </Badge>
            )}
            {filters.received_date_from && (
              <Badge variant="secondary" className="gap-1">
                From: {filters.received_date_from}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, received_date_from: ''})} />
              </Badge>
            )}
            {filters.received_date_to && (
              <Badge variant="secondary" className="gap-1">
                To: {filters.received_date_to}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, received_date_to: ''})} />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Receipts Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>LR #</TableHead>
                  <TableHead>Order #</TableHead>
                  <TableHead>Received Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Verified By</TableHead>
                  <TableHead>Gate Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceipts.length > 0 ? (
                  filteredReceipts.map((receipt) => {
                    const order = getOrderDetails(receipt.order_id);
                    return (
                      <TableRow 
                        key={receipt.receipt_id} 
                        data-testid={`receipt-row-${receipt.receipt_number}`}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/grey-receipts/${receipt.receipt_id}`)}
                      >
                        <TableCell className="font-mono font-medium">{receipt.receipt_number}</TableCell>
                        <TableCell>
                          {receipt.lr_number_transport ? (
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="outline" className="font-mono bg-teal-50 text-teal-700 border-teal-200 w-fit">
                                {receipt.lr_number_transport}
                              </Badge>
                              {receipt.lr_status && (
                                <span className="text-xs text-slate-500">{receipt.lr_status}</span>
                              )}
                            </div>
                          ) : receipt.lr_number ? (
                            <Badge variant="outline" className="font-mono bg-teal-50 text-teal-700 border-teal-200">
                              {receipt.lr_number}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasModulePermission('grey_orders', 'view') ? (
                            <Badge 
                              variant="outline" 
                              className="cursor-pointer hover:bg-slate-100"
                              onClick={(e) => { e.stopPropagation(); navigate(`/grey-orders/${receipt.order_id}`); }}
                            >
                              {order.order_number || receipt.order_id}
                            </Badge>
                          ) : (
                            <span className="text-sm text-slate-600">{order.order_number || receipt.order_id}</span>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(receipt.received_date)}</TableCell>
                        <TableCell>
                          {receipt.location_name ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {receipt.location_name}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatNumber(receipt.received_quantity)}</span>
                          <span className="text-slate-500 ml-1">Bales</span>
                          {receipt.received_material_quantity != null && (
                            <div className="text-xs text-slate-500">
                              {formatNumber(receipt.received_material_quantity)} {receipt.material_unit || ''}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{receipt.verified_by}</TableCell>
                        <TableCell>
                          <Badge className={gateStatusColors[receipt.gate_status] || gateStatusColors['Received']}>
                            {receipt.gate_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={receipt.discrepancy_notes}>
                          {receipt.discrepancy_notes || '-'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {hasModulePermission('grey_receipts', 'delete') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteTarget(receipt)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      <PackageCheck className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      No receipts found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Receipt</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete receipt <strong>{deleteTarget?.receipt_number}</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GreyReceiptsList;
